/**
 * Capitalizes the first letter of each word in a string.
 * @param {string} text - The string to be formatted.
 * @returns {string} A string with the first letter of each word capitalized.
 */
function capitalizeFirstLetterOfEachWord(text) {
  return text.toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase());
}

/**
 * Displays a message when no search results for products are found.
 * @param {HTMLElement} component - The DOM element where the message will be inserted.
 * @param {object} searchSettings - Settings for the search data from header-4 liquid.
 * @param {string} searchSettings.notFoundItem - The message to display when no items are found. results are found.
 * @returns {Promise<void>} A promise that resolves after displaying the message.
 */
async function productNotFound(component, searchSettings) {
  return new Promise((res) => {
    component.innerHTML = `
      <div class="new-search-no-result">
        <p>${searchSettings.notFoundItem}</p>
      </div>
    `;
    res();
  })
}

/**
 * Generates HTML to display search results for products.
 * @param {HTMLElement} component - The DOM element where the search results will be inserted.
 * @param {object} data - The product data obtained from the search.
 * @param {object} searchSettings - Settings for the search data from header-4 liquid.
 * @param {string} searchSettings.titleSuggest - The title for the suggestion section.
 * @param {string} searchSettings.titleProduct - The title for the product section.
 * @param {string} searchSettings.shopAllButton - The label for the "Shop All" button.
 * @param {number} limit - Limit of results to display.
 * @param {string} searchQuery - The search term for the products.
 * @returns {Promise<void>} A promise that resolves after inserting the results into the DOM.
 */

async function productItem(component, data, searchSettings, limit, searchQuery) {
  return new Promise((res) => {
    // suggets 
    const suggetsHtml = data.slice(0, limit).map((item) => `
      <li class="new-search-result-suggets-nav-item">
        <a href="${item.url}" class="new-search-result-suggets-nav-item__link">
        ${capitalizeFirstLetterOfEachWord(item.title)}
        </a>
      </li>
    `).join('')

    // products
    const itemsHtml = data.slice(0, limit).map((item) => `
      <li class="new-search-result-product-wrapper__item">
        <a class="new-search-result-product-card" href="${item.url}">
          <img src="${item.image}" alt="${item.title}" class="new-search-result-product-card__img">
          <div class="new-search-result-product-card-info">
            <p class="new-search-result-product-card-info__title">${item.title}</p>
            <p class="new-search-result-product-card-info__price">$${item.price}</p>
          </div>
        </a>
      </li>
    `).join('')

    component.innerHTML += `
    <div class="new-search-result-suggest-container" >
      <nav class="new-search-result-suggest-wrapper">
        <h3 class="new-search-result-suggets_title">${searchSettings.titleSuggest}</h3>
        <ul class="new-search-result-suggets-nav">
          ${suggetsHtml}
        </ul>
        <a href="/search?type=product&q=${encodeURIComponent(searchQuery)}" class="new-search-result-suggest-btn">${searchSettings.shopAllButton} (${data.length})</a>
      </nav>
    </div>
    <nav class="new-search-result-product">
      <h3 class="new-search-result-product__title">${searchSettings.titleProduct}</h3>
      <ul class="new-search-result-product-wrapper">
      ${itemsHtml}
      </ul>
    </nav>
    `;
    res();
  })
}

/**
 * Performs a debounced search for products to avoid excessive searches.
 * @param {string} searchQuery - The search term for the products.
 * @returns {Promise<object>} A promise that resolves with the search data.
 * @description This function performs a debounced search for products to avoid making excessive requests to the server. It delays the search for 300 milliseconds after the user stops typing to prevent overloading the server.
 */

function searchProductsDebounced(searchQuery) {
  let timerId;
  return new Promise((resolve, reject) => {
    clearTimeout(timerId);
    timerId = setTimeout(async () => {
      try {
        const data = await searchProducts(searchQuery);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    }, 300);
  });
}

/**
 * Makes a product search request to the server.
 * @param {string} searchQuery - The search term for the products.
 * @returns {Promise<object>} A promise that resolves with the search data.
 */
async function searchProducts(searchQuery) {
  const url = `search/suggest.json?q=${encodeURIComponent(searchQuery)}&resources[type]=product`;
  const response = await fetch(window.Shopify.routes.root + url);
  const data = await response.json();
  const productData = data.resources.results.products
  return productData;
}

// normal functions
//  desktop search bar
/**
 * Toggles the visibility of the desktop search bar.
 * @param {HTMLElement} searchComponent - The DOM element representing the search bar container.
 * @param {HTMLElement} searchForm - The DOM element representing the search form.
 * @param {HTMLElement} searchInput - The DOM element representing the search input field.
 */
function toggleSearchBar(searchComponent, searchForm, searchInput) {
  searchComponent.classList.toggle('active');
  if (searchComponent.classList.contains('active')) {
    searchForm.classList.add('show-form');
    searchInput.focus();
  } else {
    searchForm.classList.remove('show-form');
  }
}

/**
 * Closes the desktop search bar.
 * @param {HTMLElement} searchComponent - The DOM element representing the search bar container.
 */
function closeSearchBar(searchComponent) {
  searchComponent.classList.remove('active');
}

/**
 * Toggles the visibility of the mobile drawer.
 * @param {HTMLElement} searchDrawer - The DOM element representing the mobile drawer container.
 * @param {HTMLElement} bestSellers - The DOM element representing the best sellers container.
 * @param {HTMLElement} productElementWrapper - The DOM element representing the product element wrapper.
 */
function toggleMobileDrawer(searchDrawer, bestSellers, productElementWrapper) {
  if (searchDrawer.classList.contains('active')) {
    searchDrawer.classList.remove('active');
    document.body.classList.remove('no-scroll');
    bestSellers.classList.add('active');
    productElementWrapper.classList.remove('active');
  } else {
    searchDrawer.classList.add('active');
    document.body.classList.add('no-scroll');
  }
}


/**
 * Initializes new search functionality for both desktop and mobile versions.
 * @returns {void}
 */
export function newSearchFunctionallity() {
  // new search component

  // Selecting necessary DOM elements
  const searchElementDesktop = document.querySelector('#search-desktop');
  const searchButton = document.querySelector('.new-search-component-desktop');
  const closeSearchButton = document.querySelector('.search-bar-desktop--close');
  const searchFromDesktop = document.querySelector('.search-menu-container-form');
  const productElementWrapperDesktop = document.querySelector('.new-search-result');
  const searchComponent = document.querySelector('.search-submenu');

  // Setting limit for displayed items
  let limitItems = 4;
  if (window.innerWidth >= 995 && window.innerWidth <= 1150) {
    limitItems = 3;
  }

  // Desktop search functionality
  if (searchElementDesktop) {
    let lastSearchQuery = '';
    searchElementDesktop.addEventListener('input', async (e) => {
      const searchQuery = e.target.value.trim();
      if (searchQuery !== '' && searchQuery !== lastSearchQuery) {
        try {
          const data = await searchProductsDebounced(searchQuery);
          productElementWrapperDesktop.innerHTML = '';
          if (data.length === 0) {
            await productNotFound(productElementWrapperDesktop, searchSettings);
            productElementWrapperDesktop.classList.add('search-active');
          } else {
            await productItem(productElementWrapperDesktop, data, searchSettings, limitItems, searchQuery);
            if (productElementWrapperDesktop) {
              productElementWrapperDesktop.classList.add('search-active');
            }
          }
          lastSearchQuery = searchQuery;
        } catch (error) {
          console.error('Error:', error);
        }
      } else if (searchQuery === '') {
        productElementWrapperDesktop.innerHTML = '';
        productElementWrapperDesktop.classList.remove('search-active');
        lastSearchQuery = '';
      }
    });
  }

  // Toggling search bar visibility on desktop
  searchButton.addEventListener('click', () => {
    toggleSearchBar(searchComponent, searchFromDesktop, searchElementDesktop);
  });

  // Closing search bar on desktop
  closeSearchButton.addEventListener('click', () => {
    closeSearchBar(searchComponent);
  });

  // Mobile drawer search functionality

  // Selecting necessary DOM elements for mobile
  const searchDrawer = document.getElementById('search-drawer');
  const bestSellers = document.querySelector('.new-search-bestsellers');
  const openDrawerButton = document.querySelector('.new-search-component-mobile');
  const closeDrawerButton = document.querySelector('.search-drawer-close');
  const productElementWrapperMobile = document.querySelector('.new-search-result-mobile');

  /**
 * Closes the desktop search bar.
 * @param {HTMLElement} searchComponent - The DOM element representing the search bar container.
 */
  if (searchDrawer) {
    let lastSearchQuery = '';
    searchDrawer.addEventListener('input', async (e) => {
      const searchQuery = e.target.value.trim();
      if (searchQuery !== lastSearchQuery && searchQuery !== '') {
        try {
          const data = await searchProductsDebounced(searchQuery);
          productElementWrapperMobile.innerHTML = '';
          if (data.length === 0) {
            await productNotFound(productElementWrapperMobile, searchSettings);
            bestSellers.classList.remove('active');
            productElementWrapperMobile.classList.add('active');
          } else {
            await productItem(productElementWrapperMobile, data, searchSettings, limitItems, searchQuery);
            bestSellers.classList.remove('active');
            productElementWrapperMobile.classList.add('active');
          }
          lastSearchQuery = searchQuery;
        } catch (error) {
          console.error('Error:', error);
        }
      } else if (searchQuery === '') {
        bestSellers.classList.add('active');
        productElementWrapperMobile.classList.remove('active');
        lastSearchQuery = '';
      }
    });
  }

  // Opening mobile drawer
  openDrawerButton.addEventListener('click', () => {
    toggleMobileDrawer(searchDrawer, bestSellers, productElementWrapperMobile);
  });

  // Closing mobile drawer
  closeDrawerButton.addEventListener('click', () => {
    toggleMobileDrawer(searchDrawer, bestSellers, productElementWrapperMobile);
  });
}